// /api/sysadmin/auth/* hooks. Kept in their own file so the customer-
// app code path never imports them (any future tree-shaking should keep
// the admin auth chunk out of the main bundle).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useMe, type SystemAdminRole } from "./useAuth";

export interface SysadminMe {
  id: string;
  role: SystemAdminRole;
  // True once /mfa-enroll has been called for this admin row. False
  // means the admin hasn't started enrollment yet.
  mfaEnrolled: boolean;
  // Set once the admin entered a valid 6-digit code. Persistent — a
  // returning admin keeps mfaVerifiedAt set forever, so this flag alone
  // doesn't tell us whether the current admin session cookie is live.
  mfaVerifiedAt: string | null;
  // True when cw_admin_sess is set + matches this admin row. AdminShell
  // routes to /admin/mfa-enroll when this is false even if mfaVerifiedAt
  // is set, because /api/sysadmin/* needs the cookie.
  sessionActive: boolean;
  user: {
    id: string;
    email: string;
    displayName: string | null;
  };
}

export function useSysadminMe() {
  return useQuery<SysadminMe | null>({
    queryKey: ["sysadmin", "me"],
    queryFn: async () => {
      try {
        return await api<SysadminMe>("/api/sysadmin/auth/me");
      } catch (err) {
        // 403 = not a SystemAdmin. Treated as null so the calling
        // component can route to /admin and the gate kicks in.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export interface MfaEnrollResult {
  // otpauth:// URL the user could paste into a different authenticator
  // if QR scanning isn't available.
  otpauth: string;
  // Base64 data-URL of the QR PNG. Render directly in an <img src> —
  // no third-party QR JS needed.
  qrSvgDataUrl: string;
  // Plaintext secret returned ONCE. The UI shows it under the QR so
  // the user can type it manually as fallback. Never echoed by /me.
  secret: string;
}

export function useMfaEnroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MfaEnrollResult>("/api/sysadmin/auth/mfa-enroll", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sysadmin", "me"] });
    },
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api<{ ok: true; firstVerify: boolean }>("/api/sysadmin/auth/mfa-verify", {
        method: "POST",
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sysadmin", "me"] });
    },
  });
}

export function useSysadminLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true }>("/api/sysadmin/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(["sysadmin", "me"], null);
      // Don't blow away the customer session — admin logout only clears
      // the cw_admin_sess cookie, the user stays signed in to the
      // customer app for normal work.
    },
  });
}

// ─── Impersonation ──────────────────────────────────────────────────────

export interface ImpersonationState {
  active: boolean;
  target?: {
    id: string;
    email: string;
    displayName: string | null;
  };
}

export function useImpersonation() {
  // Gate the query on isSystemAdmin so non-admin customers (the vast
  // majority) don't poll a 401-returning endpoint every 60s. The
  // ImpersonationBanner reads this hook from inside AppShell so it
  // runs on every customer's app load.
  const { data: me } = useMe();
  const enabled = Boolean(me?.isSystemAdmin);
  return useQuery<ImpersonationState>({
    queryKey: ["sysadmin", "impersonate"],
    queryFn: async () => {
      try {
        return await api<ImpersonationState>("/api/sysadmin/impersonate");
      } catch (err) {
        // 401/403 = no admin session (e.g. admin without an active
        // cw_admin_sess cookie) → treat as inactive so the banner
        // stays hidden.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return { active: false };
        }
        throw err;
      }
    },
    enabled,
    // Refetch on window focus + every 60s so the banner doesn't lag
    // behind a cookie-expiry timeout for too long.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
}

export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) =>
      api<{ ok: true; target: { id: string; email: string; displayName: string | null } }>(
        "/api/sysadmin/impersonate",
        {
          method: "POST",
          body: JSON.stringify({ targetUserId }),
        },
      ),
    onSuccess: () => {
      // Every per-user cache is now wrong — we're a different user.
      qc.invalidateQueries();
    },
  });
}

export function useExitImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true }>("/api/sysadmin/impersonate/exit", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries();
    },
  });
}
